import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader } from "lucide-react";
const RequestCategoryForm = React.lazy(() =>
  import("./RequestCategoryForm").then((module) => ({ default: module.RequestCategoryForm }))
);
import { Button } from "../ui/button";
import { useUpdateRequestCategory } from "@/hooks/useUpdateRequestCategory";
import { useRequestGallery } from "./RequestGalleryContext";

const editCategorySchema = z.object({
  name: z.string().min(1, "Category name is required."),
  description: z.string().min(1, "Description is required."),
  groups: z.array(z.string()),
});

export const EditRequestCategoryForm: React.FC = () => {
  const { editingCategory, handleBack } = useRequestGallery();
  const methods = useForm({
    resolver: zodResolver(editCategorySchema),
    defaultValues: {
      name: editingCategory?.name || "",
      description: editingCategory?.description || "",
      groups: editingCategory?.groups || [],
    },
  });

  const updateCategoryMutation = useUpdateRequestCategory();

  const onSubmit = async (data: z.infer<typeof editCategorySchema>) => {
    if (!editingCategory) return;
    try {
      await updateCategoryMutation.mutateAsync({
        id: editingCategory._id,
        name: data.name,
        description: data.description,
        groups: data.groups,
      });
      handleBack();
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-3">
          <React.Suspense fallback={<Loader className="animate-spin" />}>
            <RequestCategoryForm />
          </React.Suspense>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleBack}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={updateCategoryMutation.isPending}>
            {updateCategoryMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
